<template>
	<div
		class="mobile-menu-wrapper"
		:class="[
			isHomePage ? 'mobile-menu-wrapper--home' : 'mobile-menu-wrapper--no_home',
			expandMenuContent ? 'mobile-menu-wrapper--expand' : ''
		]"
	>
		<div class="mobile-menu-placeholder"></div>
		<div class="mobile-menu-main">
			<div class="mobile-menu-info" @click="backHome">
				<img :src="irLogo" alt="" class="mobile-menu-logo" />
				<span class="mobile-menu-name">{{ getOrganizationName }}</span>
			</div>
			<template v-if="showMenuEntry">
				<div v-show="expandMenuContent" class="mobile-menu-close" @click="switchMenu"></div>
				<SvgIcons
					v-show="!expandMenuContent"
					name="icon_hamburger"
					class="mobile-menu-hamburger"
					@click="switchMenu"
				/>
			</template>
		</div>
		<div
			class="mobile-menu-slide"
			:class="{
				'mobile-menu-slide--disabled': !expandMenuContent
			}"
		>
			<transition name="slide">
				<div v-if="expandMenuContent" class="mobile-menu-content">
					<template v-for="(menu, index) in menus">
						<div
							:key="index + '_br'"
							v-if="index !== 0"
							class="mobile-menu-br thinner-border-bottom-sp"
						></div>
						<div :key="index + '_row'" class="mobile-menu-row" @click="handleMenuItem(menu, index)">
							<div class="mobile-menu-row-info">
								<div v-if="isSlideTmpl" class="mobile-menu-row-icon">
									<SvgIcons
										v-if="menu.menuLogoSource === 0"
										:name="getMenuLogo(menu)"
										class="mobile-svg-icon"
									/>
									<el-image v-else :src="menu.menuLogo" fit="cover" class="mobile-img-icon" />
								</div>
								<div class="mobile-menu-row-name text-overflow">{{ menu.name }}</div>
							</div>
							<SvgIcons name="right-light" class="mobile-menu-row-right" />
						</div>
					</template>
				</div>
			</transition>
		</div>
	</div>
</template>

<script setup>
import { useCommonData, useRedirect, useStoreCore } from '@/hooks';
import { useStore } from '@/utils/vue-api';
import { computed, ref, getCurrentInstance, watch } from 'vue';
import { useSlide } from '@rebuild/hooks/useSlide';

const props = defineProps({
	menus: {
		type: Array,
		default: () => []
	}
});
const emits = defineEmits(['change']);

const store = useStore();
const irLogo = computed(() => store.getters.getBaseData.logo);

const { getOrganizationName, isSlideTmpl } = useCommonData();
const { getMenuLogo } = useSlide();

const { shouldShowUI } = useStoreCore();
const showMenuEntry = computed(() => {
	if (!shouldShowUI('menuBar')) return false;
	// 三级的详情页不适合直接 replace 菜单，暂时隐藏入口
	if (isDetailPage.value) return false;
	return props.menus.length > 0;
});
const expandMenuContent = ref(false);
const switchMenu = () => {
	expandMenuContent.value = !expandMenuContent.value;
};
const handleMenuItem = (item, index) => {
	emits('change', { item, index });
	switchMenu();
};

const { replaceLocationToHome } = useRedirect();
const backHome = () => {
	if (shouldShowUI('home')) replaceLocationToHome();
};

const isHomePage = ref(true);
const isDetailPage = ref(false);
const vm = getCurrentInstance();
watch(
	() => vm.proxy.$route,
	async newRoute => {
		isHomePage.value = newRoute.name.toLowerCase().includes('home');
		isDetailPage.value = newRoute.name.toLowerCase().includes('detail');
		if (expandMenuContent.value) expandMenuContent.value = false;
	},
	{ immediate: true }
);
</script>

<style lang="less" scoped>
.slide-enter-active,
.slide-leave-active {
	transition: all 0.5s ease;
}
.slide-enter, .slide-leave-to /* .slide-leave-active 在Vue 2.1.8+中 */ {
	transform: translateX(100%);
}

.mobile-menu-wrapper {
	position: relative;
	z-index: 999;

	&--home {
		.mobile-menu-main {
			background: #fff;
		}
	}
	&--no_home {
		.mobile-menu-main {
			background: #fff;
		}
	}
	&--expand {
		.mobile-menu-main {
			background: #fff;
		}
	}

	.mobile-menu-placeholder {
		height: var(--b-menu-height);
	}
	.mobile-menu-main {
		position: fixed;
		top: 0;
		left: 0;
		right: 0;
		padding: 0 12px;
		height: var(--b-menu-height);
		display: flex;
		justify-content: space-between;
		align-items: center;
	}
	.mobile-menu-info {
		display: flex;
		align-items: center;
	}
	.mobile-menu-logo {
		min-width: 20px;
		height: 20px;
		mix-blend-mode: multiply; // 融合背景色, 隐藏白底
	}
	.mobile-menu-name {
		margin-left: 8px;
		color: #1d2129;

		font-size: 16px;

		font-weight: 500;
		line-height: normal;
	}
	.mobile-menu-hamburger {
		font-size: 20px;
		color: #4e5969 !important;
	}
	.mobile-menu-close {
		font-family: 'iconfont' !important;
		font-size: 20px;

		-webkit-font-smoothing: antialiased;
		-moz-osx-font-smoothing: grayscale;
		color: #4e5969;

		&:before {
			content: '\e621';
		}
	}

	.mobile-menu-slide {
		position: fixed;
		top: var(--b-menu-height);
		left: 0;
		right: 0;
		height: calc(100vh - var(--b-menu-height));
		background: transparent;
		overflow: hidden;

		&--disabled {
			pointer-events: none;
		}
		.mobile-menu-content {
			height: 100%;
			padding: 0 20px;
			background: #fff;
			overflow-y: auto;
			padding-right: 38px;
			margin-right: -18px;
		}
		.mobile-menu-br {
			margin: 6px 0;
		}
		.mobile-menu-row {
			padding: 8px 0;
			display: flex;
			flex-wrap: wrap;
			justify-content: space-between;
			align-items: center;

			&:first-child {
				margin-top: 14px;
			}
			&:last-child {
				margin-bottom: 14px;
			}

			.mobile-menu-row-info {
				flex: 1;
				width: 0;
				display: flex;
				align-items: center;
			}
			.mobile-menu-row-icon {
				margin-right: 12px;
				display: flex;
				align-items: center;

				.mobile-svg-icon {
					font-size: 20px;
					color: var(--theme-color);
				}
				.mobile-img-icon {
					width: 20px;
					height: 20px;
				}
			}
			.mobile-menu-row-name {
				color: var(--t-emphasis-color, #1d2129);

				font-size: 16px;

				font-weight: 500;
				line-height: 24px; /* 150% */
				max-width: 80%;
			}
			.mobile-menu-row-right {
				margin-left: 4px;
				font-size: 16px;
				color: #4e5969 !important;
			}
		}
	}
}
</style>
